import { useMutation, useQuery } from "@tanstack/react-query";
import { useInvalidate, useNotification, useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router";

import { api } from "@api/api";
import { ApiError } from "@shared/types";
import { Notification } from "@modules/notifications/types";
import { NotificationFormValues, toNotificationPayload } from "@modules/notifications/validations";

const RESOURCE = "v1/notifications_emails_admin";

const apiErrorMessage = (error: ApiError) => {
  const data: any = error.response?.data;
  if (!data) return (error as unknown as Error).message;
  if (typeof data === "string") return data;
  return data.detail ?? data.error ?? Object.values(data).flat().join(" ");
};

export function useCreateNotificationMutation() {
  const translate = useTranslate();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { open } = useNotification();

  return useMutation<Notification, ApiError, NotificationFormValues>({
    mutationFn: async (values) => {
      const { data } = await api.post<Notification>(`${RESOURCE}/`, toNotificationPayload(values));
      return data;
    },
    onSuccess: (notification) => {
      invalidate({ resource: RESOURCE, invalidates: ["list"] });
      open?.({ type: "success", message: translate("notifications_emails_admin.success.create") });
      navigate(`/notifications?tab=${notification.notification_type}`);
    },
    onError: (error) => {
      open?.({ type: "error", message: apiErrorMessage(error) });
    },
  });
}

/** Não dispara para os destinatários: e-mail vai só para test_email; push vai só para test_token/test_user ou roda em dry_run no tópico. */
export function useTestNotificationMutation() {
  const translate = useTranslate();
  const { open } = useNotification();

  return useMutation<
    any,
    ApiError,
    { values: NotificationFormValues; testEmail?: string; testToken?: string; testUser?: number }
  >({
    mutationFn: async ({ values, testEmail, testToken, testUser }) => {
      const { data } = await api.post(`${RESOURCE}/test/`, {
        ...toNotificationPayload(values),
        test_email: testEmail,
        test_token: testToken,
        test_user: testUser,
      });
      return data;
    },
    onSuccess: (data) => {
      // push: o resultado detalhado aparece na tela de revisão
      if (data?.summary) return;
      open?.({
        type: "success",
        message: translate("notifications_emails_admin.success.testEmail", { email: data?.sent_to }),
      });
    },
    onError: (error) => {
      open?.({ type: "error", message: apiErrorMessage(error) });
    },
  });
}

export interface NotificationSendLog {
  id: number;
  created_at: string;
  channel: string;
  target: string;
  status: "success" | "error" | "skipped";
  message_id: string;
  detail: string;
  dry_run: boolean;
}

export function useNotificationSendLogsQuery(id: number | null) {
  return useQuery<NotificationSendLog[]>({
    queryKey: [RESOURCE, id, "logs"],
    queryFn: async () => (await api.get<NotificationSendLog[]>(`${RESOURCE}/${id}/logs/`)).data,
    enabled: id !== null,
  });
}

export interface FcmUser {
  id: number;
  full_name: string;
  email: string;
  platforms: string[];
  last_seen: string;
}

/** Usuários com token FCM no convênio, para o teste de push. */
export function useFcmUsersQuery(agreement: number | null | undefined, search: string) {
  return useQuery<FcmUser[]>({
    queryKey: [RESOURCE, "fcm-users", agreement, search],
    queryFn: async () =>
      (await api.get<FcmUser[]>(`${RESOURCE}/fcm-users/`, { params: { agreement, search: search || undefined } })).data,
    enabled: !!agreement,
  });
}
